import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellSlash, EnvelopeSimple, PaperPlaneTilt, UserCircle, UsersThree } from "@phosphor-icons/react";
import { aender, api, lies } from "../api/client";
import type { Ich, Nutzer } from "../api/types";
import { useIch } from "../App";
import { Auswahl } from "../components/Auswahl";
import { Dialog, Feld, Meldung } from "../components/Bausteine";
import { datum } from "../lib/format";
import { usePfad } from "../lib/pfad";

const ROLLE: Record<Nutzer["rolle"], string> = { admin: "Admin", buero: "Büro", extern: "Extern" };

/** Verwaltung (Rückmeldung 24.10.2026): das eigene Konto für alle, Benutzer für Admins. */
export function Verwaltung() {
  usePfad([{ titel: "Verwaltung" }]);
  const ich = useIch().data;
  if (!ich) return null;
  return (
    <main className="seite">
      <div className="seite-kopf"><div><h1>Verwaltung</h1>
        <div className="dim">{ich.rolle === "admin" ? "Eigenes Konto und Benutzer" : "Eigenes Konto"}</div></div></div>
      <MeinKonto ich={ich} />
      <Erinnerungen ich={ich} />
      {ich.rolle !== "extern" && <Versandmail ich={ich} />}
      {ich.rolle === "admin" && <Benutzer ich={ich} />}
    </main>
  );
}

function MeinKonto({ ich }: { ich: Ich }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ anzeigename: ich.anzeigename ?? "", kuerzel: ich.kuerzel, firma: ich.firma, telefon: ich.telefon });
  const [ok, setOk] = useState(false);
  const speichern = useMutation({
    mutationFn: () => aender<Ich>("/me", f),
    onSuccess: (neu) => { qc.setQueryData(["ich"], neu); setOk(true); },
  });
  return (
    <section className="karte">
      <div className="karte-kopf"><h2><UserCircle size={18} /> Mein Konto</h2><span className="mute">{ich.email} · {ROLLE[ich.rolle]}</span></div>
      <div className="raster">
        <Feld titel="Name">
          <input className="eingabe" value={f.anzeigename} onChange={(e) => { setF({ ...f, anzeigename: e.target.value }); setOk(false); }} />
        </Feld>
        <Feld titel="Kürzel"><input className="eingabe" maxLength={4} value={f.kuerzel} onChange={(e) => { setF({ ...f, kuerzel: e.target.value }); setOk(false); }} /></Feld>
        <Feld titel="Firma"><input className="eingabe" value={f.firma} onChange={(e) => { setF({ ...f, firma: e.target.value }); setOk(false); }} /></Feld>
        <Feld titel="Telefon"><input className="eingabe" value={f.telefon} onChange={(e) => { setF({ ...f, telefon: e.target.value }); setOk(false); }} /></Feld>
      </div>
      <div className="punkt-leiste" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={!f.anzeigename.trim() || speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
        {ok && <span className="gespeichert">gespeichert</span>}
        <span className="mute">Der Name steht in neuen Protokollen unter „Erstellt“.</span>
      </div>
      <Meldung fehler={speichern.error} />
    </section>
  );
}

function Benutzer({ ich }: { ich: Ich }) {
  const qc = useQueryClient();
  const liste = useQuery({ queryKey: ["nutzer"], queryFn: () => lies<Nutzer[]>("/admin/nutzer") });
  const [einladen, setEinladen] = useState(false);
  const aendern = useMutation({
    mutationFn: ({ id, r }: { id: string; r: Partial<Nutzer> }) => aender(`/admin/nutzer/${id}`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nutzer"] }),
  });
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2><UsersThree size={18} /> Benutzer</h2>
        <span className="mute">Wer sich anmelden darf und mit welcher Rolle</span>
        <div className="luft" />
        <button className="knopf haupt" onClick={() => setEinladen(true)}><PaperPlaneTilt size={16} /> Einladen</button>
      </div>
      <Meldung fehler={liste.error || aendern.error} />
      <div className="liste">
        {liste.data?.map((u) => (
          <div key={u.id} className={`zeile${u.aktiv ? "" : " still"}`}>
            <span className="nr"><UserCircle size={20} /></span>
            <div className="haupt">
              <div>{u.anzeigename || u.email}
                {u.erinnerung === "aus" && <span className="chip" title="Hat Erinnerungsmails abbestellt" style={{ marginLeft: 6 }}>
                  <BellSlash size={12} /> keine Erinnerungen</span>}
                {u.erinnerung === "alle" && <span className="chip" title="Erinnerungen auch für Projekte in Bearbeitung" style={{ marginLeft: 6 }}>
                  <Bell size={12} /> alle Projekte</span>}</div>
              <div className="mute">{[u.anzeigename && u.email, u.firma,
                u.login_count ? `zuletzt ${datum(u.last_login_at)}` : "eingeladen, noch nie angemeldet",
                `${u.projekte} Projekte`].filter(Boolean).join(" · ")}</div>
            </div>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Auswahl style={{ width: "auto" }} value={u.rolle} disabled={u.id === ich.id}
                       onChange={(e) => aendern.mutate({ id: u.id, r: { rolle: e.target.value as Nutzer["rolle"] } })}>
                {Object.entries(ROLLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Auswahl>
              {u.id !== ich.id && (
                <button className="knopf leise" onClick={() => (u.aktiv ? confirm(`${u.email} stilllegen? Die Person wird sofort abgemeldet.`) : true)
                  && aendern.mutate({ id: u.id, r: { aktiv: !u.aktiv } })}>{u.aktiv ? "Stilllegen" : "Freischalten"}</button>
              )}
            </span>
          </div>
        ))}
      </div>
      <p className="mute">Büro-Adressen (@lilasp.de) können sich auch ohne Einladung selbst anmelden. Externe kommen nur über eine
        Einladung herein und sehen nur die Projekte, in die sie aufgenommen werden (Reiter „Zugang“ im Projekt).</p>
      {einladen && <Einladen zu={() => setEinladen(false)} />}
    </section>
  );
}

function Einladen({ zu }: { zu: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ email: "", anzeigename: "", rolle: "buero" as Nutzer["rolle"], mail: true });
  const [ergebnis, setErgebnis] = useState("");
  const los = useMutation({
    mutationFn: () => api<{ mail: string | null }>("POST", "/admin/einladen", f),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["nutzer"] });
      if (r.mail && r.mail !== "verschickt") setErgebnis(`Angelegt, aber die Mail ging nicht raus (${r.mail}). Bitte selbst Bescheid geben.`);
      else zu();
    },
  });
  return (
    <Dialog offen zu={zu} titel="Benutzer einladen" fuss={<>
      <button className="knopf leise" onClick={zu}>{ergebnis ? "Schließen" : "Abbrechen"}</button>
      {!ergebnis && <button className="knopf haupt" disabled={!f.email.includes("@") || los.isPending} onClick={() => los.mutate()}>Einladen</button>}
    </>}>
      <div className="raster">
        <Feld titel="E-Mail" breit><input className="eingabe" type="email" autoFocus value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Feld>
        <Feld titel="Name"><input className="eingabe" value={f.anzeigename} onChange={(e) => setF({ ...f, anzeigename: e.target.value })} /></Feld>
        <Feld titel="Rolle">
          <Auswahl value={f.rolle} onChange={(e) => setF({ ...f, rolle: e.target.value as Nutzer["rolle"] })}>
            {Object.entries(ROLLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <label className="haken breit"><input type="checkbox" checked={f.mail} onChange={(e) => setF({ ...f, mail: e.target.checked })} /> Einladung mit Link per Mail schicken</label>
      </div>
      <p className="mute">Admin und Büro gehen nur mit Büro-Adressen. Externe (z. B. Bauherr) danach im Projekt unter „Zugang“ aufnehmen.</p>
      <Meldung fehler={los.error} />
      {ergebnis && <div className="meldung">{ergebnis}</div>}
    </Dialog>
  );
}

interface Vorlage { vorgabe_text: string; vorgabe_signatur: string; platzhalter: Record<string, string>; absender: string }

/** Eigener Begleittext und Signatur für den Versand von Protokollen, Niederschriften und
 *  Mängelanzeigen (SPEC §11.3). Leer = Vorgabe. Abruflink und Einwandfrist kommen immer
 *  hinein, auch wenn die eigene Vorlage sie weglässt. */
function Versandmail({ ich }: { ich: Ich }) {
  const qc = useQueryClient();
  const vorlage = useQuery({ queryKey: ["mailvorlage"], queryFn: () => lies<Vorlage>("/me/mailvorlage"), staleTime: Infinity });
  const [text, setText] = useState(ich.mail_text);
  const [signatur, setSignatur] = useState(ich.mail_signatur);
  const [ok, setOk] = useState(false);
  const [vorschauText, setVorschauText] = useState("");
  const speichern = useMutation({
    mutationFn: () => aender<Ich>("/me", { mail_text: text, mail_signatur: signatur }),
    onSuccess: (neu) => { qc.setQueryData(["ich"], neu); setOk(true); },
  });
  // Vorschau nach kurzer Tipppause, mit dem noch nicht gespeicherten Text.
  useEffect(() => {
    const t = setTimeout(() => {
      api<{ text: string }>("POST", "/me/mailvorlage/vorschau", { mail_text: text, mail_signatur: signatur })
        .then((r) => setVorschauText(r.text)).catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
  }, [text, signatur]);
  const v = vorlage.data;
  const geaendert = text !== ich.mail_text || signatur !== ich.mail_signatur;
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2><EnvelopeSimple size={18} /> Versandmail</h2>
        <span className="mute">Begleittext und Signatur beim Versand an den Verteiler{v && ` · Absender: ${v.absender}`}</span>
      </div>
      <div className="mailvorlage">
        <div className="mailvorlage-felder">
          <div className="feld">
            <div className="feld-kopf"><span>Begleittext</span>
              {v && <button className="knopf leise" onClick={() => { setText(v.vorgabe_text); setOk(false); }}>Standard übernehmen</button>}</div>
            <textarea className="eingabe mono" rows={11} value={text} placeholder={v ? `leer = Standard:\n\n${v.vorgabe_text}` : ""}
                      onChange={(e) => { setText(e.target.value); setOk(false); }} />
          </div>
          <div className="feld">
            <div className="feld-kopf"><span>Signatur</span>
              {v && <button className="knopf leise" onClick={() => { setSignatur(v.vorgabe_signatur); setOk(false); }}>Standard übernehmen</button>}</div>
            <textarea className="eingabe mono" rows={5} value={signatur} placeholder={v ? `leer = Standard:\n\n${v.vorgabe_signatur}` : ""}
                      onChange={(e) => { setSignatur(e.target.value); setOk(false); }} />
          </div>
          {v && (
            <details className="platzhalter">
              <summary className="mute">Platzhalter</summary>
              <dl>{Object.entries(v.platzhalter).map(([k, t]) => <div key={k}><dt><code>{k}</code></dt><dd>{t}</dd></div>)}</dl>
              <p className="mute">Abruflink und Einwandfrist gehören zum Zugangsnachweis: fehlen sie im eigenen Text, hängt die Anwendung sie an.</p>
            </details>
          )}
        </div>
        <div className="feld">
          <span>Vorschau (Beispiel: Protokoll)</span>
          <pre className="mail-vorschau">{vorschauText}</pre>
        </div>
      </div>
      <div className="punkt-leiste" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={!geaendert || speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
        {(text || signatur) && (
          <button className="knopf leise" onClick={() => { setText(""); setSignatur(""); setOk(false); }}>Auf Standard zurücksetzen</button>
        )}
        {ok && !geaendert && <span className="gespeichert">gespeichert</span>}
      </div>
      <Meldung fehler={speichern.error || vorlage.error} />
    </section>
  );
}

const ERINNERUNG: Record<Ich["erinnerung"], [string, string]> = {
  leitung: ["Für Projekte, die ich leite", "Vorgabe. Fristen der Projekte, in denen Sie die Projektleitung haben."],
  alle: ["Für alle meine Projekte", "Auch Projekte, in denen Sie nur bearbeiten."],
  aus: ["Keine Erinnerungsmails", "Fristen sehen Sie weiter in der Projekt-Übersicht und im Kalender-Abo."],
};

/** Erinnerungsmails je Person (SPEC §12.4, Rückmeldung 25.10.2026). Gespeichert wird beim Wählen. */
function Erinnerungen({ ich }: { ich: Ich }) {
  const qc = useQueryClient();
  const setzen = useMutation({
    mutationFn: (erinnerung: Ich["erinnerung"]) => aender<Ich>("/me", { erinnerung }),
    onSuccess: (neu) => qc.setQueryData(["ich"], neu),
  });
  const wert = setzen.isPending && setzen.variables ? setzen.variables : ich.erinnerung;
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2><Bell size={18} /> Erinnerungsmails</h2>
        <span className="mute">morgens um 6:30 eine Übersicht der Fristen – nur an Tagen, an denen etwas Neues fällig wird</span>
      </div>
      <div className="erinnerung-wahl" role="radiogroup" aria-label="Erinnerungsmails">
        {(Object.keys(ERINNERUNG) as Ich["erinnerung"][]).map((k) => (
          <label key={k} className={`erinnerung-option${wert === k ? " an" : ""}`}>
            <input type="radio" name="erinnerung" checked={wert === k} disabled={setzen.isPending}
                   onChange={() => setzen.mutate(k)} />
            <span><b>{ERINNERUNG[k][0]}</b><span className="mute">{ERINNERUNG[k][1]}</span></span>
          </label>
        ))}
      </div>
      {setzen.isSuccess && <span className="gespeichert">gespeichert</span>}
      <Meldung fehler={setzen.error} />
    </section>
  );
}
